export const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.xlsx',
  '.xls',
  '.csv',
  '.pptx',
  '.txt',
  '.md',
  '.markdown',
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
] as const

export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.join(',')

export const ACCEPTED_SUMMARY =
  'PDF, Excel (.xlsx, .xls), CSV, PowerPoint (.pptx), text, markdown, or an image'

const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  '.pdf': 'application/pdf',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.csv': 'text/csv',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
}

export function contentTypeFor(file: { name: string; type?: string | undefined }): string {
  return (
    MIME_BY_EXTENSION[extensionOf(file.name)] ?? nonEmpty(file.type) ?? 'application/octet-stream'
  )
}

function nonEmpty(value: string | undefined): string | undefined {
  return value !== undefined && value.trim() !== '' ? value : undefined
}

export type FileVerdict =
  | { kind: 'accepted' }
  | { kind: 'word-document'; message: string }
  | { kind: 'legacy-powerpoint'; message: string }
  | { kind: 'unsupported'; message: string }
  | { kind: 'too-large'; message: string }
  | { kind: 'empty'; message: string }

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot === -1 ? '' : fileName.slice(dot).toLowerCase()
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`
  const megabytes = (bytes / (1024 * 1024)).toFixed(1)
  return `${megabytes.endsWith('.0') ? megabytes.slice(0, -2) : megabytes} MB`
}

export function checkFile(file: { name: string; size: number }, maxBytes: number): FileVerdict {
  const extension = extensionOf(file.name)

  if (file.size === 0) {
    return { kind: 'empty', message: `${file.name} is empty.` }
  }

  if (file.size > maxBytes) {
    return {
      kind: 'too-large',
      message: `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(maxBytes)}.`,
    }
  }

  if (extension === '.docx' || extension === '.doc') {
    return {
      kind: 'word-document',
      message: `Word documents aren't supported. Export ${file.name} as a PDF and upload that.`,
    }
  }

  if (extension === '.ppt') {
    return {
      kind: 'legacy-powerpoint',
      message: `Only .pptx is supported, not the older .ppt. Re-save ${file.name} as .pptx.`,
    }
  }

  if (!(ACCEPTED_EXTENSIONS as readonly string[]).includes(extension)) {
    return {
      kind: 'unsupported',
      message: `${file.name} is not a supported format. Accepted: ${ACCEPTED_SUMMARY}.`,
    }
  }

  return { kind: 'accepted' }
}
